import { useState, useEffect, useRef } from 'react';
import { useRoomContext } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';

interface TranscriptionEntry {
  id: string;
  speaker: string;
  text: string;
  isFinal: boolean;
  language?: string;
  timestamp: number;
  translatedText?: string;
}

const decoder = new TextDecoder();

export function TranscriptionPanel() {
  const room = useRoomContext();
  const [entries, setEntries] = useState<TranscriptionEntry[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleData = (payload: Uint8Array) => {
      try {
        const data = JSON.parse(decoder.decode(payload));
        if (data.type === 'transcription') {
          setEntries((prev) => {
            // Replace interim with final for same speaker
            if (data.isFinal) {
              const filtered = prev.filter(
                (e) => !(e.speaker === data.speaker && !e.isFinal),
              );
              return [...filtered, {
                id: `${Date.now()}-${Math.random()}`,
                speaker: data.speaker,
                text: data.text,
                isFinal: true,
                language: data.language,
                timestamp: data.timestamp,
                translatedText: data.translatedText,
              }];
            }
            // Update or add interim
            const existing = prev.findIndex(
              (e) => e.speaker === data.speaker && !e.isFinal,
            );
            const entry: TranscriptionEntry = {
              id: existing >= 0 ? prev[existing].id : `${Date.now()}-${Math.random()}`,
              speaker: data.speaker,
              text: data.text,
              isFinal: false,
              language: data.language,
              timestamp: data.timestamp,
            };
            if (existing >= 0) {
              const updated = [...prev];
              updated[existing] = entry;
              return updated;
            }
            return [...prev, entry];
          });
        }
      } catch {
        // Ignore non-transcription data
      }
    };

    room.on(RoomEvent.DataReceived, handleData);
    return () => {
      room.off(RoomEvent.DataReceived, handleData);
    };
  }, [room]);

  useEffect(() => {
    containerRef.current?.scrollTo({ top: containerRef.current.scrollHeight, behavior: 'smooth' });
  }, [entries]);

  const formatTime = (ts: number) => {
    const d = new Date(ts);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  return (
    <div className="videocall-transcription" ref={containerRef}>
      {entries.map((entry) => (
        <div
          key={entry.id}
          className={`videocall-transcription__entry ${!entry.isFinal ? 'videocall-transcription__entry--interim' : ''}`}
        >
          <span className="videocall-transcription__time">[{formatTime(entry.timestamp)}]</span>
          <span className="videocall-transcription__speaker">{entry.speaker}: </span>
          <span>{entry.text}</span>
          {entry.translatedText && (
            <div style={{ paddingLeft: '2rem', fontStyle: 'italic', opacity: 0.8 }}>
              {entry.translatedText}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
